import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { BarChart3 } from "lucide-react";
import type { DashboardChartsResponse } from "@/hooks/use-dashboard";

interface AnalyticsChartProps {
  data?: DashboardChartsResponse;
  isLoading?: boolean;
  onRangeChange?: (days: number) => void;
}

type ChartType = "users" | "subscribers" | "revenue";

const chartConfig: Record<
  ChartType,
  { label: string; color: string; gradient: [string, string] }
> = {
  users: {
    label: "Users",
    color: "#3b82f6",
    gradient: ["#3b82f6", "#1d4ed8"],
  },
  subscribers: {
    label: "Subscribers",
    color: "#10b981",
    gradient: ["#10b981", "#059669"],
  },
  revenue: {
    label: "Revenue",
    color: "#f59e0b",
    gradient: ["#f59e0b", "#d97706"],
  },
};

const dateRanges = [
  { label: "7D", value: 7 },
  { label: "30D", value: 30 },
  { label: "90D", value: 90 },
];

function CustomTooltip({
  active,
  payload,
  label,
  chartType,
}: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
  chartType: ChartType;
}) {
  if (!active || !payload || !payload.length) return null;

  const config = chartConfig[chartType];
  const value = payload[0].value;
  const formattedValue =
    chartType === "revenue"
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 0,
        }).format(value)
      : new Intl.NumberFormat("en-US").format(value);

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-popover/95 backdrop-blur-sm border border-border rounded-lg shadow-lg p-3"
    >
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className="text-sm font-semibold" style={{ color: config.color }}>
        {formattedValue} {config.label}
      </p>
    </motion.div>
  );
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function formatYAxisValue(value: number, chartType: ChartType): string {
  if (chartType === "revenue") {
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}k`;
    }
    return `$${value}`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}k`;
  }
  return value.toString();
}

export function AnalyticsChart({
  data,
  isLoading = false,
  onRangeChange,
}: AnalyticsChartProps) {
  const [selectedRange, setSelectedRange] = useState(30);
  const [activeChart, setActiveChart] = useState<ChartType>("users");

  const handleRangeChange = (days: number) => {
    setSelectedRange(days);
    onRangeChange?.(days);
  };

  const chartData = data
    ? data[activeChart === "revenue" ? "revenue" : activeChart].map((point) => ({
        date: formatDate(point.date),
        value: point.value,
      }))
    : [];

  const config = chartConfig[activeChart];

  if (isLoading) {
    return (
      <Card className="col-span-2">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5 rounded" />
              <Skeleton className="h-6 w-32" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-9 w-64 rounded-lg" />
              <Skeleton className="h-9 w-24 rounded-lg" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="w-full h-[300px] rounded-lg" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="col-span-2">
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg font-semibold">Analytics Overview</CardTitle>
          </div>

          <div className="flex items-center gap-3">
            {/* Chart Type Selector */}
            <div className="flex items-center bg-muted rounded-lg p-1">
              {(Object.keys(chartConfig) as ChartType[]).map((type) => (
                <button
                  key={type}
                  onClick={() => setActiveChart(type)}
                  className={cn(
                    "px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200",
                    activeChart === type
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {chartConfig[type].label}
                </button>
              ))}
            </div>

            {/* Date Range Selector */}
            <div className="flex items-center bg-muted rounded-lg p-1">
              {dateRanges.map((range) => (
                <button
                  key={range.value}
                  onClick={() => handleRangeChange(range.value)}
                  className={cn(
                    "px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200",
                    selectedRange === range.value
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeChart}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full h-[300px]"
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient
                    id={`gradient-${activeChart}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={config.color} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={config.color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  dy={10}
                  interval="preserveStartEnd"
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  dx={-10}
                  tickFormatter={(value) => formatYAxisValue(value, activeChart)}
                />
                <Tooltip
                  content={({ active, payload, label }) => (
                    <CustomTooltip
                      active={active}
                      payload={payload as Array<{ value: number }>}
                      label={label}
                      chartType={activeChart}
                    />
                  )}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={config.color}
                  strokeWidth={2}
                  fill={`url(#gradient-${activeChart})`}
                  animationDuration={1000}
                  animationEasing="ease-out"
                />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}

export function AnalyticsChartSkeleton() {
  return (
    <Card className="col-span-2">
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-6 w-32" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-64 rounded-lg" />
            <Skeleton className="h-9 w-24 rounded-lg" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Skeleton className="w-full h-[300px] rounded-lg" />
      </CardContent>
    </Card>
  );
}
